import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Alert,
  Snackbar,
  IconButton,
  InputAdornment,
  Chip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  ExpandMore,
  Email,
  Sms,
  Payment,
  Security,
  CheckCircle,
  Error,
  Refresh,
} from '@mui/icons-material';
import axios from 'axios';

// Temporary Grid wrapper to fix build issues
const Grid = ({ container, item, spacing, xs, sm, md, lg, children, ...props }: any) => {
  if (container) {
    return <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: spacing || 3, ...props.sx }} {...props}>{children}</Box>;
  }
  return <Box {...props}>{children}</Box>;
};

interface ConnectionSettings {
  // SMTP Settings
  smtp_host: string;
  smtp_port: string;
  smtp_user: string;
  smtp_pass: string;
  smtp_from: string;
  smtp_secure: string;
  
  // Twilio Settings
  twilio_account_sid: string;
  twilio_auth_token: string;
  twilio_phone_number: string;
  
  // Stripe Settings
  stripe_secret_key: string;
  stripe_publishable_key: string;
  stripe_webhook_secret: string;
  
  // PayFast Settings
  payfast_merchant_id: string;
  payfast_merchant_key: string;
  payfast_passphrase: string;
  
  // General Settings
  frontend_url: string;
  backend_url: string;
  support_email: string;
  api_key: string;
}

const Connections: React.FC = () => {
  const [connections, setConnections] = useState<ConnectionSettings>({} as ConnectionSettings);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'info' });
  const [testResults, setTestResults] = useState<Record<string, { success: boolean; message: string }>>({});

  useEffect(() => {
    loadConnections();
  }, []);

  const loadConnections = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/connections');
      setConnections(response.data.connections);
    } catch (error) {
      console.error('Failed to load connections:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load connection settings',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof ConnectionSettings, value: string) => {
    setConnections(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put('/api/admin/connections', { connections });
      setSnackbar({
        open: true,
        message: 'Service connections updated successfully!',
        severity: 'success'
      });
      await loadConnections(); // Reload to get any processed values
    } catch (error) {
      console.error('Failed to update connections:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update service connections',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async (service: string) => {
    try {
      const response = await axios.post('/api/admin/connections/test', { service });
      setTestResults(prev => ({
        ...prev,
        [service]: response.data
      }));
      
      setSnackbar({
        open: true,
        message: response.data.success ? 'Connection test successful!' : 'Connection test failed',
        severity: response.data.success ? 'success' : 'error'
      });
    } catch (error) {
      console.error(`Failed to test ${service} connection:`, error);
      setSnackbar({
        open: true,
        message: `Failed to test ${service} connection`,
        severity: 'error'
      });
    }
  };

  const togglePasswordVisibility = (field: string) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const isPasswordField = (field: string) => {
    return field.includes('pass') || field.includes('token') || field.includes('key') || field.includes('secret');
  };

  const getFieldIcon = (field: string) => {
    if (field.startsWith('smtp')) return <Email />;
    if (field.startsWith('twilio')) return <Sms />;
    if (field.startsWith('stripe') || field.startsWith('payfast')) return <Payment />;
    return <Security />;
  };

  const getServiceStatus = (service: string) => {
    const result = testResults[service];
    if (!result) return null;
    
    return result.success ? (
      <Chip icon={<CheckCircle />} label="Connected" color="success" size="small" />
    ) : (
      <Chip icon={<Error />} label="Failed" color="error" size="small" />
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Service Connections
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadConnections}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Manage your service connections for email, SMS, and payment processing. 
        Changes are applied immediately and services are reloaded automatically.
      </Alert>

      <Grid container spacing={3}>
        {/* SMTP Settings */}
        <Grid item xs={12} md={6}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Email />
                <Typography variant="h6">Email (SMTP)</Typography>
                {getServiceStatus('smtp')}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="SMTP Host"
                    value={connections.smtp_host || ''}
                    onChange={(e) => handleInputChange('smtp_host', e.target.value)}
                    placeholder="smtp.gmail.com"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="SMTP Port"
                    value={connections.smtp_port || ''}
                    onChange={(e) => handleInputChange('smtp_port', e.target.value)}
                    placeholder="587"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="SMTP Username"
                    value={connections.smtp_user || ''}
                    onChange={(e) => handleInputChange('smtp_user', e.target.value)}
                    placeholder="your-email@gmail.com"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="SMTP Password"
                    type={showPasswords.smtp_pass ? 'text' : 'password'}
                    value={connections.smtp_pass || ''}
                    onChange={(e) => handleInputChange('smtp_pass', e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => togglePasswordVisibility('smtp_pass')}
                            edge="end"
                          >
                            {showPasswords.smtp_pass ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="From Email"
                    value={connections.smtp_from || ''}
                    onChange={(e) => handleInputChange('smtp_from', e.target.value)}
                    placeholder="noreply@yourdomain.com"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Secure Connection"
                    value={connections.smtp_secure || ''}
                    onChange={(e) => handleInputChange('smtp_secure', e.target.value)}
                    placeholder="true"
                    helperText="true for SSL/TLS, false for none"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    onClick={() => handleTestConnection('smtp')}
                    startIcon={<CheckCircle />}
                  >
                    Test SMTP Connection
                  </Button>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Twilio Settings */}
        <Grid item xs={12} md={6}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Sms />
                <Typography variant="h6">SMS (Twilio)</Typography>
                {getServiceStatus('twilio')}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Account SID"
                    value={connections.twilio_account_sid || ''}
                    onChange={(e) => handleInputChange('twilio_account_sid', e.target.value)}
                    placeholder="AC..."
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Auth Token"
                    type={showPasswords.twilio_auth_token ? 'text' : 'password'}
                    value={connections.twilio_auth_token || ''}
                    onChange={(e) => handleInputChange('twilio_auth_token', e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => togglePasswordVisibility('twilio_auth_token')}
                            edge="end"
                          >
                            {showPasswords.twilio_auth_token ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={connections.twilio_phone_number || ''}
                    onChange={(e) => handleInputChange('twilio_phone_number', e.target.value)}
                    placeholder="+1234567890"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    onClick={() => handleTestConnection('twilio')}
                    startIcon={<CheckCircle />}
                  >
                    Test Twilio Connection
                  </Button>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Stripe Settings */}
        <Grid item xs={12} md={6}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Payment />
                <Typography variant="h6">Stripe</Typography>
                {getServiceStatus('stripe')}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Secret Key"
                    type={showPasswords.stripe_secret_key ? 'text' : 'password'}
                    value={connections.stripe_secret_key || ''}
                    onChange={(e) => handleInputChange('stripe_secret_key', e.target.value)}
                    placeholder="sk_live_..."
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => togglePasswordVisibility('stripe_secret_key')}
                            edge="end"
                          >
                            {showPasswords.stripe_secret_key ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Publishable Key"
                    value={connections.stripe_publishable_key || ''}
                    onChange={(e) => handleInputChange('stripe_publishable_key', e.target.value)}
                    placeholder="pk_live_..."
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Webhook Secret"
                    type={showPasswords.stripe_webhook_secret ? 'text' : 'password'}
                    value={connections.stripe_webhook_secret || ''}
                    onChange={(e) => handleInputChange('stripe_webhook_secret', e.target.value)}
                    placeholder="whsec_..."
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => togglePasswordVisibility('stripe_webhook_secret')}
                            edge="end"
                          >
                            {showPasswords.stripe_webhook_secret ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    onClick={() => handleTestConnection('stripe')}
                    startIcon={<CheckCircle />}
                  >
                    Test Stripe Connection
                  </Button>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* PayFast Settings */}
        <Grid item xs={12} md={6}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Payment />
                <Typography variant="h6">PayFast</Typography>
                {getServiceStatus('payfast')}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Merchant ID"
                    value={connections.payfast_merchant_id || ''}
                    onChange={(e) => handleInputChange('payfast_merchant_id', e.target.value)}
                    placeholder="10000000"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Merchant Key"
                    type={showPasswords.payfast_merchant_key ? 'text' : 'password'}
                    value={connections.payfast_merchant_key || ''}
                    onChange={(e) => handleInputChange('payfast_merchant_key', e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => togglePasswordVisibility('payfast_merchant_key')}
                            edge="end"
                          >
                            {showPasswords.payfast_merchant_key ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Passphrase"
                    type={showPasswords.payfast_passphrase ? 'text' : 'password'}
                    value={connections.payfast_passphrase || ''}
                    onChange={(e) => handleInputChange('payfast_passphrase', e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => togglePasswordVisibility('payfast_passphrase')}
                            edge="end"
                          >
                            {showPasswords.payfast_passphrase ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    onClick={() => handleTestConnection('payfast')}
                    startIcon={<CheckCircle />}
                  >
                    Test PayFast Connection
                  </Button>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* General Settings */}
        <Grid item xs={12}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Security />
                <Typography variant="h6">General Settings</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Frontend URL"
                    value={connections.frontend_url || ''}
                    onChange={(e) => handleInputChange('frontend_url', e.target.value)}
                    placeholder="https://yourdomain.com"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Backend URL"
                    value={connections.backend_url || ''}
                    onChange={(e) => handleInputChange('backend_url', e.target.value)}
                    placeholder="https://api.yourdomain.com"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Support Email"
                    value={connections.support_email || ''}
                    onChange={(e) => handleInputChange('support_email', e.target.value)}
                    placeholder="support@yourdomain.com"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="API Key"
                    type={showPasswords.api_key ? 'text' : 'password'}
                    value={connections.api_key || ''}
                    onChange={(e) => handleInputChange('api_key', e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => togglePasswordVisibility('api_key')}
                            edge="end"
                          >
                            {showPasswords.api_key ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          {saving ? 'Updating...' : 'Update All Connections'}
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Connections; 